import React, { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  Incident,
  IncidentGroup,
  IncidentKpis,
  IncidentOpsQueuesResponse,
  IncidentReviewOutcomeRollupResponse,
} from '../../services/incidentsApi';
import MetricCard from './MetricCard';
import { buildIncidentOverviewMetrics } from '../../pages/analytics/incidentAnalyticsModel';
import { resolveCanonicalReviewPopulation } from '../../domain/incidents/reviewPopulationMetrics';

export interface HeroMetricRowProps {
  kpis: IncidentKpis | null;
  opsQueues: IncidentOpsQueuesResponse | null;
  groups: IncidentGroup[];
  filteredGroups?: IncidentGroup[];
  filteredIncidents?: Incident[];
  filteredIncidentTotal?: number | null;
  tableMode: 'grouped' | 'raw';
  layout?: 'cards' | 'strip';
  reviewOutcomeRollup?: IncidentReviewOutcomeRollupResponse | null;
  reviewPopulationCompatible?: boolean;
  groupedReviewerHealth?: {
    staleAwaitingReview: number;
    awaitingReview: number;
  } | null;
  onOpenIncidents: () => void;
  onHighRiskUnresolved: () => void;
  onRecurringHotspots: () => void;
  onReviewThroughput: () => void;
  onOpenReviewerHealth?: () => void;
}

type HeroMetricCardModel = {
  key: string;
  label: string;
  value: number;
  context: string;
  tone: 'accent' | 'danger' | 'dormant' | 'warning' | 'softAmber' | 'success';
  visualWeight: 'primary' | 'default';
  onClick?: () => void;
};

function asCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
}

export function sumGroupedObservationCount(groups: IncidentGroup[]): number {
  return (Array.isArray(groups) ? groups : []).reduce(
    (sum, group) => sum + asCount(group?.alert_count),
    0,
  );
}

export function countRecordedReviewOutcomes(rows: Incident[]): number {
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const outcome = String(
      row?.review_outcome
      || (row as { safety_case?: { review?: { review_outcome?: unknown } } })?.safety_case?.review?.review_outcome
      || '',
    ).trim();
    return Boolean(outcome);
  }).length;
}

const HeroMetricRow: React.FC<HeroMetricRowProps> = ({
  kpis,
  opsQueues,
  groups,
  filteredGroups,
  filteredIncidents,
  filteredIncidentTotal,
  tableMode,
  layout = 'cards',
  reviewOutcomeRollup,
  reviewPopulationCompatible = true,
  groupedReviewerHealth,
  onOpenIncidents,
  onHighRiskUnresolved,
  onRecurringHotspots,
  onReviewThroughput,
  onOpenReviewerHealth,
}) => {
  const theme = useTheme();
  const investigateStrip = layout === 'strip';
  const cardModels = useMemo(() => {
    const hasGroupedTablePopulation = tableMode === 'grouped' && filteredGroups !== undefined;
    const hasRawTablePopulation = tableMode === 'raw' && filteredIncidents !== undefined;
    const hasVisibleTablePopulation = hasGroupedTablePopulation || hasRawTablePopulation;
    const activeGroups = hasGroupedTablePopulation ? filteredGroups : groups;
    const {
      openPatternCases,
      openObservations,
      highRiskUnresolved,
      highSeverityObservations,
      overdueCount,
      recurringItems,
      closedToday,
      newToday,
    } = buildIncidentOverviewMetrics({ kpis, opsQueues, groups: activeGroups });
    const confirmedRisk = Number(reviewOutcomeRollup?.customer_risk_total || 0);
    const reviewPopulation = resolveCanonicalReviewPopulation(reviewOutcomeRollup);
    const rawTableRows = hasRawTablePopulation ? filteredIncidents : [];
    const knownRawTotal = typeof filteredIncidentTotal === 'number' && Number.isFinite(filteredIncidentTotal)
      ? Math.max(0, Math.trunc(filteredIncidentTotal))
      : null;
    const tableObservationTotal = hasGroupedTablePopulation
      ? sumGroupedObservationCount(filteredGroups)
      : hasRawTablePopulation
        ? (knownRawTotal != null ? Math.max(knownRawTotal, rawTableRows.length) : rawTableRows.length)
        : (tableMode === 'raw' ? openObservations : sumGroupedObservationCount(activeGroups));
    const reviewedFromRawTable = hasRawTablePopulation
      ? countRecordedReviewOutcomes(rawTableRows)
      : null;
    const rollupMatchesTable = reviewOutcomeRollup != null
      && reviewPopulation.total === tableObservationTotal;
    let reviewedTotal = reviewPopulation.reviewed;
    let reviewedScopeTotal = reviewPopulation.total;
    let pendingReviewTotal = reviewPopulation.pending;
    let canShowReviewPopulation = reviewOutcomeRollup != null && reviewPopulationCompatible;
    if (canShowReviewPopulation && hasVisibleTablePopulation) {
      reviewedScopeTotal = tableObservationTotal;
      if (rollupMatchesTable) {
        pendingReviewTotal = reviewPopulation.pending;
      } else if (reviewedFromRawTable != null) {
        reviewedTotal = reviewedFromRawTable;
        pendingReviewTotal = Math.max(0, tableObservationTotal - reviewedTotal);
      } else if (reviewPopulation.reviewed === 0) {
        reviewedTotal = 0;
        pendingReviewTotal = tableObservationTotal;
      } else {
        canShowReviewPopulation = false;
      }
    }
    const uncertainRisk = Number(
      Array.isArray(reviewOutcomeRollup?.risk_disposition_items)
        ? ((reviewOutcomeRollup?.risk_disposition_items.find((item) => String(item?.risk_disposition || '') === 'uncertain_risk')?.count) || 0)
        : 0,
    );
    const reviewedContext = reviewedScopeTotal <= 0
      ? 'No observations in this range yet'
      : reviewedTotal <= 0
        ? `${reviewedScopeTotal} observations in scope, none reviewed yet`
        : !reviewPopulation.ratioAllowed
          ? `${reviewedTotal} observations have a recorded review outcome in this range`
        : reviewedTotal >= reviewedScopeTotal
          ? `All ${reviewedScopeTotal} in-scope observation${reviewedScopeTotal === 1 ? '' : 's'} have a recorded review outcome`
          : `${reviewedTotal} of ${reviewedScopeTotal} in-scope observation${reviewedScopeTotal === 1 ? '' : 's'} have a recorded review outcome`;

    const highRiskContext = confirmedRisk > 0 || uncertainRisk > 0
        ? `${confirmedRisk} confirmed safety concern${confirmedRisk === 1 ? '' : 's'}${uncertainRisk > 0 ? ` • ${uncertainRisk} evidence-limited` : ''}`
        : overdueCount > 0
          ? `${overdueCount} approaching SLA`
          : 'None waiting right now';

    const isGrouped = tableMode === 'grouped';
    const isRaw = tableMode === 'raw';
    const openPrimaryLabel = isRaw
      ? 'Open observations'
      : investigateStrip
        ? 'Needs attention'
        : 'Patterns needing attention';
    const rawOpenObservationCount = tableObservationTotal;
    const openPrimaryValue = isRaw ? rawOpenObservationCount : openPatternCases;
    const openPrimaryContext = isRaw
      ? rawOpenObservationCount > 0
        ? 'In this time range'
        : 'No observations in this time range'
      : openPatternCases > 0
        ? 'Open recurring risk patterns in this review window'
        : 'None need attention right now';

    const groupedHighRiskLabel = investigateStrip ? 'High severity' : 'High-severity patterns';
    const groupedRecurringLabel = investigateStrip ? 'Recurring' : 'Recurring patterns';
    const recurringContext = recurringItems > 0
      ? investigateStrip
        ? 'Needs review'
        : `${recurringItems} ${recurringItems === 1 ? 'pattern needs' : 'patterns need'} review`
      : investigateStrip
        ? 'None in this period'
        : 'No recurring patterns this period';
    const rawHighRiskValue = hasRawTablePopulation
      ? rawTableRows.filter((row) => {
          const severity = String(row?.severity || '').toLowerCase();
          return severity === 'high' || severity === 'critical';
        }).length
      : highSeverityObservations;
    const rawHighRiskLabel = investigateStrip ? 'High severity' : 'High-severity observations';
    const rawHighRiskContext = rawHighRiskValue > 0
      ? 'In this time range'
      : 'None in this time range';
    const rawOverdueContext = overdueCount > 0
      ? 'Current queue, not limited to this time window'
      : 'No overdue items in the current queue';

    const cards: HeroMetricCardModel[] = [
      {
        key: 'open',
        label: openPrimaryLabel,
        value: isRaw ? rawOpenObservationCount : openPrimaryValue,
        context: openPrimaryContext,
        tone: 'accent' as const,
        visualWeight: 'primary' as const,
        onClick: onOpenIncidents,
      },
      {
        key: 'high_risk',
        label: isGrouped ? groupedHighRiskLabel : rawHighRiskLabel,
        value: isGrouped ? highRiskUnresolved : rawHighRiskValue,
        context: isGrouped ? highRiskContext : rawHighRiskContext,
        tone: (isGrouped ? highRiskUnresolved : rawHighRiskValue) > 0 ? ('danger' as const) : ('dormant' as const),
        visualWeight: 'default' as const,
        onClick: onHighRiskUnresolved,
      },
      {
        key: 'hotspots',
        label: isGrouped ? groupedRecurringLabel : 'Overdue observations',
        value: isGrouped ? recurringItems : overdueCount,
        context: isGrouped ? recurringContext : rawOverdueContext,
        tone:
          (isGrouped ? recurringItems : overdueCount) > 0
            ? confirmedRisk > 0
              ? ('warning' as const)
              : ('softAmber' as const)
            : ('softAmber' as const),
        visualWeight: 'default' as const,
        onClick: onRecurringHotspots,
      },
    ];
    // PATTERNS: reviewed_total only; omit the tile when rollup is unavailable.
    if (isGrouped && canShowReviewPopulation) {
      cards.push({
        key: 'observations_reviewed',
        label: 'Observations reviewed',
        value: reviewedTotal,
        context: reviewedContext,
        tone: 'success' as const,
        visualWeight: 'default' as const,
        onClick: onReviewThroughput,
      });
    } else if (isRaw) {
      if (canShowReviewPopulation) {
        cards.push({
          key: 'pending_review',
          label: investigateStrip ? 'Pending review' : 'Pending observation review',
          value: pendingReviewTotal,
          context: pendingReviewTotal > 0
            ? `${pendingReviewTotal} in this filtered view without a recorded review outcome`
            : 'No pending review in this time range',
          tone: pendingReviewTotal > 0 ? ('warning' as const) : ('success' as const),
          visualWeight: 'default' as const,
          onClick: onReviewThroughput,
        });
        cards.push({
          key: 'observations_reviewed',
          label: 'Observations reviewed',
          value: reviewedTotal,
          context: reviewedContext,
          tone: 'success' as const,
          visualWeight: 'default' as const,
          onClick: onReviewThroughput,
        });
      } else if (!reviewOutcomeRollup) {
        const closedContext = closedToday > 0
          ? newToday > 0
            ? `+${newToday} new in the same range`
            : 'In this time range'
          : 'No closures in this range yet';
        cards.push({
          key: 'closed_today',
          label: 'Closed in Range',
          value: closedToday,
          context: closedContext,
          tone: 'success' as const,
          visualWeight: 'default' as const,
          onClick: onReviewThroughput,
        });
      }
    }
    if (groupedReviewerHealth && isGrouped) {
      cards.push({
        key: 'review_health',
        label: investigateStrip ? 'Stale reviews' : 'Stale pattern reviews',
        value: groupedReviewerHealth.staleAwaitingReview,
        context: groupedReviewerHealth.staleAwaitingReview > 0
          ? `${groupedReviewerHealth.awaitingReview} waiting for review • follow-up blocked`
          : groupedReviewerHealth.awaitingReview > 0
            ? `${groupedReviewerHealth.awaitingReview} waiting, none stale yet`
            : investigateStrip
              ? 'No stale reviews'
              : 'No stale grouped pattern reviews',
        tone: groupedReviewerHealth.staleAwaitingReview > 0 ? ('warning' as const) : ('success' as const),
        visualWeight: 'default' as const,
        onClick: onOpenReviewerHealth || onRecurringHotspots,
      });
    }
    return cards;
  }, [filteredGroups, filteredIncidentTotal, filteredIncidents, groupedReviewerHealth, groups, investigateStrip, kpis, onHighRiskUnresolved, onOpenIncidents, onOpenReviewerHealth, onRecurringHotspots, onReviewThroughput, opsQueues, reviewOutcomeRollup, reviewPopulationCompatible, tableMode]);

  if (investigateStrip) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          py: 0.5,
        }}
      >
        {cardModels.map((card, index) => {
          const valueColor =
            card.tone === 'danger'
              ? '#8c1d18'
              : card.tone === 'warning'
                ? '#7c2d12'
                : card.tone === 'accent'
                  ? theme.palette.primary.dark
                  : card.tone === 'success'
                    ? '#256846'
                    : card.tone === 'dormant'
                      ? '#64748b'
                      : theme.palette.text.primary;
          return (
          <Box
            key={card.key}
            onClick={card.onClick}
            sx={{
              flex: '1 1 0',
              minWidth: { xs: '46%', md: 0 },
              px: { xs: 1, md: 1.5 },
              py: 0.75,
              cursor: card.onClick ? 'pointer' : 'default',
              borderRight: {
                xs: 'none',
                md: index < cardModels.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.7)}` : 'none',
              },
              '&:hover': card.onClick
                ? { bgcolor: alpha(theme.palette.action.hover, 0.4), borderRadius: 1 }
                : undefined,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.02em' }}>
              {card.label}
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, mt: 0.2, color: valueColor }}>
              {card.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, lineHeight: 1.35 }}>
              {card.context}
            </Typography>
          </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cardModels.map((card) => (
        <Grid item xs={12} sm={6} xl={3} key={card.key}>
          <MetricCard
            label={card.label}
            value={card.value}
            context={card.context}
            tone={card.tone}
            visualWeight={card.visualWeight ?? 'default'}
            onClick={card.onClick}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default HeroMetricRow;
